import ZTable, { IColumnType, ITableRef } from '@/components/ZTable';
import { DEFAULT_DATE_FORMAT, ModalWidth, dateTimeDisplayFormat } from '@/config';
import { DownCircleFilled, StopFilled, ThunderboltOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import MainContent from '@/layouts/MainContent';
import { Button, Modal, Tag, Timeline, Tooltip } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import SearchForm from './SearchForm';
import UpdateStatusModal from './UpdateStatusModal';
import { TripStatusType } from '@/constants';
import UpdateTripDetailModal from './UpdateTripDetailModal';
import { TripDetailDto } from '@/__generated';
import useHasPermissions, { useCanManageFleetTrip } from '@/hooks/useHasPermissions';
import ExportPassengerModal from './ExportPassengerModal';
import { APP_PERMISSIONS } from '@/services/roleService';
import MapTab from '../TripsCalendar/components/MapTab';
import { RouteAndTripIcon } from '@/assets/icons/entities/_';

const PAGE_TITLE = 'tripDetailsPage_Title';

export default function TripDetailsPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [trip, setTrip] = React.useState<TripDetailDto>();
  const [editStatusTrip, setEditStatusTrip] = React.useState<TripDetailDto>();
  const [tripDetail, setTripDetail] = React.useState<TripDetailDto>();
  const [exportPassengerTrip, setExportPassengerTrip] = React.useState<TripDetailDto>();
  const canUpdate = useCanManageFleetTrip();
  const canExportReport = useHasPermissions([APP_PERMISSIONS.ExportTravelerReport]);
  const [tripMap, setTripMap] = React.useState<TripDetailDto>();
  const columns = React.useMemo(() => {
    return [
      {
        title: 'tripDetailsPage_TripRefNumber',
        dataIndex: 'tripRefNumber',
        ellipsis: true,
      },
      {
        title: 'tripDetailsPage_TripName',
        dataIndex: 'tripName',
        ellipsis: true,
        width: 250,
      },
      {
        title: 'tripDetailsPage_SeatingModel',
        dataIndex: 'seatingModelName',
        ellipsis: true,
      },
      {
        title: 'tripDetailsPage_SourceStation',
        dataIndex: 'sourceName',
        ellipsis: true,
      },
      {
        title: 'tripDetailsPage_DestinationStation',
        dataIndex: 'destinationName',
        ellipsis: true,
      },
      {
        title: 'tripDetailsPage_Route',
        dataIndex: 'routeName',
        ellipsis: true,
      },
      {
        title: 'tripDetailsPage_DepartureDate',
        dataIndex: 'plannedDepartureDateTime',
        ellipsis: true,
        render: v => (v ? dateTimeDisplayFormat.originDateTime(v) : ''),
        width: 160,
      },
      {
        title: 'tripDetailsPage_Status',
        dataIndex: 'status',
        ellipsis: true,
        align: 'center',
        render: v => <Tag color={TripStatusType.__getColor(v)}>{TripStatusType.__getValue(v)}</Tag>,
        width: 120,
      },
      {
        title: 'tripDetailsPage_Stops',
        dataIndex: 'tripStops',
        align: 'center',
        ellipsis: true,
        width: 100,
        render: (v, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setTrip(record);
            }}
          >
            <b>{v?.length}</b>
          </Button>
        ),
      },
    ] as IColumnType<TripDetailDto>[];
  }, []);
  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          defaultFilter={{ departureDate: moment().format(DEFAULT_DATE_FORMAT) }}
          url="/fleets/trip-details/get-trip-details"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          actionColumnWidth={150}
          allowEdit={false}
          allowDelete={false}
          searchBtnAlignLeft
          searchForm={SearchForm}
          extraActions={[
            {
              render: record => (
                <Tooltip title={t('Map')}>
                  <Button size="small" onClick={() => setTripMap(record)} type="text" icon={<RouteAndTripIcon width={20} />} />
                </Tooltip>
              ),
            },
            canExportReport
              ? {
                  render: record => {
                    return (
                      <Tooltip title={t('tripDetailsPage_ExportPassenger')}>
                        <Button size="small" onClick={() => setExportPassengerTrip(record)} type="primary" danger icon={<FilePdfOutlined />} />
                      </Tooltip>
                    );
                  },
                }
              : null,
            canUpdate
              ? {
                  render: record => {
                    return (
                      <Tooltip title={t('tripDetailsPage_ChangeStatus')}>
                        <Button size="small" onClick={() => setEditStatusTrip(record)} type="primary" icon={<ThunderboltOutlined />} />
                      </Tooltip>
                    );
                  },
                }
              : null,
            canUpdate
              ? {
                  render: record => (
                    <Tooltip title={t('zTable.edit')}>
                      <Button
                        disabled={record.status !== TripStatusType.Scheduled}
                        danger
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setTripDetail(record);
                        }}
                      ></Button>
                    </Tooltip>
                  ),
                }
              : null,
          ]}
        ></ZTable>
      </div>
      {trip ? (
        <Modal
          okButtonProps={{ style: { display: 'none' } }}
          cancelText={t('Close')}
          onCancel={v => setTrip(undefined)}
          open={true}
          width={ModalWidth.small}
          title={t('routesPage_RouteStops')}
        >
          <Timeline mode={'right'}>
            <Timeline.Item
              dot={<DownCircleFilled style={{ fontSize: 20 }} />}
              label={dateTimeDisplayFormat.originDateTime(trip.plannedDepartureDateTime)}
            >
              {trip.sourceName}
            </Timeline.Item>
            {trip?.tripStops?.map((stop, index) => {
              return (
                <Timeline.Item key={index} label={dateTimeDisplayFormat.originDateTime(stop.plannedDepartureDateTime)}>
                  {stop.stationName}
                </Timeline.Item>
              );
            })}

            <Timeline.Item dot={<StopFilled style={{ fontSize: 20, color: '#cf1322' }} />}>{trip.destinationName}</Timeline.Item>
          </Timeline>
        </Modal>
      ) : null}
      {editStatusTrip ? (
        <UpdateStatusModal
          onClose={reload => {
            setEditStatusTrip(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
          trip={editStatusTrip}
        />
      ) : null}
      {tripDetail ? (
        <UpdateTripDetailModal
          onClose={reload => {
            setTripDetail(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
          tripDetail={tripDetail}
        />
      ) : null}

      {exportPassengerTrip ? <ExportPassengerModal trip={exportPassengerTrip} onClose={() => setExportPassengerTrip(undefined)} /> : null}

      {tripMap ? (
        <Modal
          open={true}
          maskClosable={false}
          width={ModalWidth.large}
          className="nta-custom-form"
          title={tripMap.tripRefNumber}
          forceRender={true}
          style={{ top: 10 }}
          okText={t('save')}
          cancelText={t('Close')}
          okButtonProps={{
            style: { display: 'none' },
          }}
          onCancel={() => setTripMap(undefined)}
        >
          <MapTab trip={tripMap} />
        </Modal>
      ) : null}
    </MainContent>
  );
}
